export enum SalesOrderStatus {
  QUOTE = 'quote',
  ORDER = 'order',
  CONFIRMED = 'confirmed',
  PARTIALLY_SHIPPED = 'partially_shipped',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface SalesOrderItem {
  id: string;
  productId: string;
  productName: string;
  // Sent so far on delivery challans
  dispatchedQuantity?: number;
  quantity: number;
  unitPrice: number;
  discountRate: number; // percentage
  discountAmount: number;
  taxRate: number; // percentage
  taxAmount: number;
  totalAmount: number;
  totalPrice: number;
}

export interface SalesOrder {
  id: string;
  plantId?: string | null;
  orderNumber: string;
  // As a quotation: its revision, when it went to the customer, and how long the prices hold
  quoteRevision?: number;
  quoteSentAt?: string | null;
  quoteSentTo?: string | null;
  quoteValidUntil?: string | null;
  customerId: string;
  customer?: {
    id: string;
    name: string;
    code: string;
    email: string;
    phone: string;
  };
  orderDate: string;
  expectedDeliveryDate: string;
  status: SalesOrderStatus;
  totalAmount: number;
  taxAmount: number;
  discountAmount: number;
  grandTotal: number;
  items: SalesOrderItem[];
  notes?: string;
  shippingAddress?: string;
  
  // Delivery Terms
  deliveryTerms?: string;
  paymentTerms?: string;
  shippingMethod?: string;
  deliveryLocation?: string;
  specialInstructions?: string;
  
  // Promised Dates Tracking
  promisedDate?: string;
  confirmedDate?: string;
  actualShipDate?: string;
  actualDeliveryDate?: string;
  deliveryDelayReason?: string;
  promiseVarianceDays?: number;
  isDeliveryOnTime?: boolean;
  
  createdAt: string;
  updatedAt: string;
}

export interface CreateSalesOrderRequest {
  customerId: string;
  plantId?: string;
  quoteValidUntil?: string;
  // Needed when a quotation that has been sent is changed
  revisionReason?: string;
  orderDate: string;
  expectedDeliveryDate: string;
  items: {
    productId: string;
    quantity: number;
    unitPrice: number;
    discountRate?: number;
    taxRate?: number;
    // False makes the server keep the price sent here instead of looking one up
    usePriceList?: boolean;
  }[];
  notes?: string;
  shippingAddress?: string;
  
  // Delivery Terms
  deliveryTerms?: string;
  paymentTerms?: string;
  shippingMethod?: string;
  deliveryLocation?: string;
  specialInstructions?: string;

  // Promised Dates Tracking
  promisedDate?: string;
  confirmedDate?: string;
  actualShipDate?: string;
  actualDeliveryDate?: string;
  deliveryDelayReason?: string;
}

export interface SalesOrderQueryParams {
  page: number;
  limit: number;
  search?: string;
  status?: SalesOrderStatus;
  sortBy?: SalesOrderSortField;
  sortOrder?: 'ASC' | 'DESC';
}

export type SalesOrderSortField = 'createdAt' | 'orderDate' | 'expectedDeliveryDate' | 'grandTotal';

export interface PaginatedSalesOrders {
  data: SalesOrder[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SalesOrderSummary {
  total: number;
  quotes: number;
  open: number;
  delivered: number;
  cancelled: number;
  overdue: number;
  revenue: number;
  quoteValue: number;
}

export interface SalesOrderFilters {
  status?: SalesOrderStatus;
  customerId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface DeliveryPerformanceReport {
  totalOrders: number;
  onTimeDeliveries: number;
  lateDeliveries: number;
  onTimePercentage: number;
  avgVarianceDays: number;
  maxVarianceDays: number;
  minVarianceDays: number;
}

export interface PromisedDatesUpdate {
  promisedDate?: string;
  confirmedDate?: string;
  actualShipDate?: string;
  actualDeliveryDate?: string;
  deliveryDelayReason?: string;
}

export type SalesDashboardRange = '30d' | '90d' | '12m' | 'all';

export interface SalesDashboardData {
  range: SalesDashboardRange;
  periodStart: string | null;
  generatedAt: string;
  revenue: {
    value: number;
    orders: number;
    previousValue: number | null;
    changePercent: number | null;
  };
  averageOrderValue: number;
  openOrders: number;
  overdueOrders: number;
  quotes: { count: number; value: number };
  ordersByStatus: { status: string; count: number; value: number }[];
  monthlyRevenue: { month: string; revenue: number; orders: number }[];
  topCustomers: { id: string; name: string; orders: number; revenue: number }[];
  topProducts: { productId: string; name: string; quantity: number; revenue: number }[];
  recentOrders: {
    id: string;
    orderNumber: string;
    customerName: string;
    orderDate: string;
    status: string;
    grandTotal: number;
    itemCount: number;
  }[];
  overdueList: {
    id: string;
    orderNumber: string;
    customerName: string;
    expectedDeliveryDate: string;
    status: string;
    grandTotal: number;
    daysOverdue: number;
  }[];
  deliveryPerformance: {
    deliveredOrders: number;
    onTime: number;
    late: number;
    onTimePercentage: number | null;
    averageDelayDays: number | null;
  };
}
